import { useEffect, useRef, useState } from "react";
import { Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../../../layouts/Default";
import { Main } from "../../../../themes/Default/components/Main";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Button, ButtonBack } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { Form } from "../../../../themes/Default/components/Form";
import { Row } from "../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../themes/Default/components/Form/components/Row/components/Field";
import { groupsManagement } from "../../../../hooks/api/GroupsManagement";
import { useTranslation } from "react-i18next";
import { Title } from "../../../../themes/Default/components/Title";
import { Slide, toast, ToastContainer } from "react-toastify";
import SummerNote from "../../../../packages/SummerNote/SummerNote";
import { plansManagement } from "../../../../hooks/api/PlansManagement/PlansManagement";
import SelectPlan from "../../../../themes/Default/components/Form/components/Select/SelectPlan";
import { useUserToken } from "../../../../hooks/core/UserToken";
import { roles } from "../../../../services/utils/Roles";
import { Select } from "../../../../themes/Default/components/Form/components/Select";
import { employeesManagement } from "../../../../hooks/api/EmployeesManagement";
import { prepareSelect } from "../../../../services/core/Select";

const EditPlan = () => {
    const { t } = useTranslation("common");
    const { groupId } = useParams();
    const { planId } = useParams();
    const { dateId } = useParams();
    const { classIndex } = useParams();
    const formRef = useRef(null);
    const navigate = useNavigate();
    const [formErrors, setFormErrors] = useState([]);
    const [groupName, setGroupName] = useState("");
    const [hasTests, setHasTests] = useState(false);
    const [hasProgress, setHasProgress] = useState(false);
    const [hasReport, setHasReport] = useState(false);
    const { useViewGroupMutation, useUpdatePlanMutation, useViewPlanMutation, useApprovePlanMutation } = groupsManagement;
    const [viewGroup] = useViewGroupMutation();
    const [updatePlan] = useUpdatePlanMutation();
    const [viewPlan] = useViewPlanMutation();
    const [approvePlan] = useApprovePlanMutation();
    const [disableApprove, setDisableApprove] = useState(true);
    const [saving, setSaving] = useState(false);
    const [plans, setPlans] = useState();
    const { usePlansListMutation } = plansManagement;
    const [planList, { isLoading }] = usePlansListMutation();
    const [numberOfClasses, setNumberOfClasses] = useState("");
    const [{ getDecoded }] = useUserToken();
    const decodedToken = getDecoded();
    const [date, setDate] = useState([]);
    const [unformatedDate, setUnformatedDate] = useState([]);
    const { useEmployeesListMutation } = employeesManagement;
    const [professorList] = useEmployeesListMutation();
    const [professors, setProfessors] = useState([]);
    const {usePlanNoteListMutation} = groupsManagement;
    const [notesList] = usePlanNoteListMutation();
    const [totalNotes, setTotalNotes] = useState(false);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const isFutureOrToday = unformatedDate >= today;

    const tabs = [
        { title: t("groups.attendance"), onClick: () => gotoAttendanceTab(groupId) },
        { title: t("groups.planning"), isActive: true },
        ...(hasProgress ? [
            { title: t('groups.progress'), onClick: () => gotoProgressTab(groupId)},
        ] : []), 
        ...(hasTests ? [
            { title: t('groups.progressTest'), onClick: () => gotoProgressTestsTab(groupId) }
        ] : []), 
        ...(hasReport ? [
            { title: t('reports.title'),onClick: () => gotoReportTab(groupId)},
        ] : []),
        { title: t("documents"), onClick: () => gotoDocumentsTab(groupId) },
        { title: t("notes"), onClick: () => gotoNotesTab(groupId) },
    ];

    if (
        decodedToken?.roles.includes(roles.SUPER_ADMIN) ||
        decodedToken?.roles.includes(roles.SECRETARY) ||
        decodedToken?.roles.includes(roles.DIRECTOR) || 
        decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES)
    ) {
        tabs.push({
            title: t("settings.title"),
            onClick: () => gotoEditTab(groupId),
        });
    }

    const gotoAttendanceTab = (groupId) => {
        navigate("/groups/" + groupId + "/attendance");
    };

    const gotoProgressTestsTab = (groupId) => {
        navigate("/groups/" + groupId + "/tests");
    }

    const gotoReportTab = (groupId) => {
        navigate("/groups/" + groupId + "/reports");
    }

    const gotoNotesTab = (groupId) => {
        navigate("/groups/" + groupId + "/notes");
    };

    const gotoDocumentsTab = (groupId) => {
        navigate("/groups/" + groupId + "/documents");
    };

    const gotoProgressTab = (groupId) => {
        navigate("/groups/" + groupId + "/progress");
    };

    const gotoEditTab = (groupId) => {
        navigate("/groups/edit/" + groupId);
    };

    const gotoGroupPlanningNote = () => {
        navigate("/groups/" + groupId + "/planning/" + planId + "/notes/" + dateId + "/classIndex/" + classIndex);
    }

    const onApprove = () => {
        approvePlan({ groupId, planId }).then((result) => {
            if (result?.error) {
                toast.error(result?.error?.data?.error?.responseMessage);
            } else {
                toast.info(result?.data?.info?.responseMessage);
                setDisableApprove(true);
            }
        });
    };

    const onSave = () => {
        if (formRef.current) {
            setSaving(true);
            formRef.current.handleSubmit();
        }
    };

    const onSaved = (values) => {
        values.date = dateId;
        updatePlan({ body: values, groupId, planId }).then((result) => {
            if (result?.error) {
                toast.error(result.error.data.error.responseMessage);
            } else {
                toast.info(result?.data?.info?.responseMessage);
            }
            setSaving(false);
        });
    };

    const loadGroupAndPlans = () => {
        viewGroup(groupId).then((result) => {
            setGroupName(result?.data?.name);
            setHasTests(result?.data?.hasTests);
            setHasReport(result?.data?.hasReport);
            setHasProgress(result?.data?.hasProgress);
            setNumberOfClasses(result?.data?.classNumber);
            const body = {
                language: result?.data?.language,
                languageProgram: result?.data?.program,
            };
            planList(body).then((result) => {
                const plans = result?.data?.records;
                setPlans(plans);
            });
        });
    };

    const formatDateToDMY = (value) => {

        const parts = value.split("-");

        const year = parts[0];
        const month = parseInt(parts[1], 10);
        const day = parseInt(parts[2], 10);

        return `${day < 10 ? `0${day}` : day}.${month < 10 ? `0${month}` : month}.${year}`;
    };

    useEffect(() => {
        loadGroupAndPlans();
        viewPlan({ groupId, planId }).then((result) => {
            formRef.current.setValues(result?.data);
            setDisableApprove(result?.data?.isApproved);
            const dateObj = new Date(result?.data?.date);
            dateObj.setHours(0, 0, 0, 0);
            setUnformatedDate(dateObj);
            setDate(formatDateToDMY(result?.data?.date));
        });
        professorList({role: 'ROLE_PROFESSOR'}).then((result) => {
            let options = [];
            result?.data?.records.map((professor) => {
                options.push(
                    {
                        id: professor.id,
                        name: professor.firstName + ' ' + professor.lastName
                    }
                );
            })
            setProfessors(prepareSelect(options));
        });
        notesList(planId).then((result) => {
            setTotalNotes(result?.data?.totalRecords);
        });
    }, []);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to={"/groups/" + groupId + "/planning"} />
                <Button title={t("save")} icon="fa-regular fa-floppy-disk" onClick={onSave}></Button>
                {(decodedToken?.roles.includes(roles.SUPER_ADMIN) || decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES) || decodedToken?.roles.includes(roles.PROFESSOR_COORDINATOR)) && (
                    <Button title={t("approve")} icon="fa-regular fa-check" isDisabled={disableApprove} onClick={onApprove}/>
                )} 
                <Button  badgeNumber={totalNotes} title={t("Dodaj/Vidi komentare")} icon="fa-regular fa-comment-dots" onClick={() => {gotoGroupPlanningNote();}}/>           
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title fullWidth={"100%"}>
                    <span>
                        {groupName} - <span style={{ color: "#828282", fontWeight: "normal" }}>{date}</span>
                    </span>
                    <span className="numOfClasses">
                        {t("reports.classNumber")}: <span className="numOfClassesFrstNum">{classIndex}</span>
                        <span className="numOfClassesScndNum">/{numberOfClasses}</span>
                    </span>
                </Title>
                <Breadcrumps>
                    <Link title={t("groups.title")} icon="fa-light fa-users" />
                    <Link title={t("groups.planning")} />
                    <Link title={t("groups.planningAdd")} isActive={true} />
                </Breadcrumps>
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        description: "",
                    }}
                    onSubmit={onSaved}
                >
                    {({ values, handleChange, setFieldValue }) => (
                        <Form>
                            <Row>
                                <Field>
                                    <SelectPlan
                                        name="plans"
                                        label={"planovi"}
                                        onChange={(e) => {
                                            handleChange(e);
                                            setFieldValue("description", e.target.value);
                                        }}
                                        required={true}
                                        options={plans}
                                        selectedValue={""}
                                    ></SelectPlan>
                                </Field>
                                <Field></Field>
                            </Row>
                            <Row>
                                <Field isFullWidth={true}>
                                    <SummerNote
                                        height="400px"
                                        name="description"
                                        label={t("plans.description")}
                                        value={values.description}
                                        onChange={handleChange}
                                        error={formErrors?.description}
                                        saving={saving}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Select 
                                        name="substituteProfessor" 
                                        label={t('Zamenski profesor:')} 
                                        onChange={(e) => {
                                            handleChange(e);
                                            setFieldValue("substituteProfessor", e.target.value || null);
                                        }}
                                        options={[
                                            { id: "", name: t("— Bez zamenskog profesora —") },
                                            ...professors
                                        ]} 
                                        selectedValue={values.substituteProfessor || ""} 
                                        error={formErrors?.substituteProfessor}
                                        disabled={!decodedToken?.roles.includes(roles.SUPER_ADMIN) && !decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES) && !isFutureOrToday}
                                        searchInDropdown={false}
                                    />
                                </Field>
                                <Field></Field>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </Main>
        </DefaultLayout>
    );
};

export default EditPlan;
